import React from "react";
import {
    ModalBody,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingValidationAssociateIcon } from "@components/Common/SvgIcons";
import { useSnapshot } from "valtio";

import {
    setIsAddPeripheralClicked,
    setCheckModificationEffect,
    store as associateStore,
} from "@pages/Applications/DeviceAssociateModal/store";
import {
    setIsLoadingAssociateValidation,
    store as userStore,
} from "@components/VerticalLayout/store";

import Printer from "../../DeviceAssociateModal/Printer";
import { Monetics } from "../../DeviceAssociateModal/Monetics";
import CashManagement from "../../DeviceAssociateModal/CashManagementApplicationForm/CashManagement";

import { getAuthorizationStates } from "@components/VerticalLayout/StyledSidebarMenu";
import NotAuthorizationPage from "@components/VerticalLayout/NotAuthorizationPage";

import "../../index.css";
import { useSelector } from "react-redux";
import { ID_COINERS, ID_MONETICS, ID_PRINTERS } from "@constants/menu";
import {
    PERIPHERAL_TYPE_CASH_MANAGEMENT,
    PERIPHERAL_TYPE_MONETIC,
} from "@constants/index";

const TOAST_OPTIONS = {
    position: toast.POSITION.TOP_CENTER,
    autoClose: 2000,
    theme: "colored" as const,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
};

type MenuWrapperType = {
    setIsDeviceAssociateModalOpened: Function;
    oneShop: boolean;
    isDeviceAssociateModalOpened: boolean;
    rowData: any;
    dataIsUpdated: boolean | number;
    setDataIsUpdated: Function;
    selectedRows: any;
};

type TabsMenuType = {
    title: string;
    content: React.ReactNode;
    customClassName: string;
    subMenu?: {
        title: string;
        content: React.ReactNode;
    }[];
};

export function MenuWrapperAssociate({
    setIsDeviceAssociateModalOpened,
    oneShop,
    isDeviceAssociateModalOpened,
    rowData,
    dataIsUpdated,
    setDataIsUpdated,
    selectedRows,
}: MenuWrapperType) {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const authorizationList = useSelector(
        (state: any) => state.global.infoUser.profileAuthorization
    );
    const { isLoadingAssociateValidation } = useSnapshot(userStore);
    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [activeSubTab, setActiveSubTab] = React.useState<number>(0);
    const [isSubMenuCollapsed, setIsSubMenuCollapsed] = React.useState<boolean>(
        false
    );

    const notAuthModalProps = React.useMemo(() => ({
        maxHeight: "720px",
        height: "515px",
        marginTop: "15%",
    }), []);

    const sharedPeripheralProps = React.useMemo(() => ({
        rowData,
        setIsDeviceAssociateModalOpened,
        isDeviceAssociateModalOpened,
        dataIsUpdated,
        setDataIsUpdated,
        selectedRows,
    }), [
        rowData,
        setIsDeviceAssociateModalOpened,
        isDeviceAssociateModalOpened,
        dataIsUpdated,
        setDataIsUpdated,
        selectedRows,
    ]);

    const tabs: TabsMenuType[] = React.useMemo(() => {
        const tabsConfig = [
            {
                authId: ID_PRINTERS,
                title: t("Printers"),
                customClassName: "afa_sid_printerPopup",
                component: <Printer {...sharedPeripheralProps} oneShop={oneShop} />,
            },
            {
                authId: ID_MONETICS,
                title: t("Monetics"),
                customClassName: "afa_sid_moneticsPopup",
                component: <Monetics {...sharedPeripheralProps} />,
            },
            {
                authId: ID_COINERS,
                title: t("Coiners"),
                customClassName: "afa_sid_coinersPopup",
                component: <CashManagement {...sharedPeripheralProps} />,
            },
        ];

        return tabsConfig
            .filter(
                ({ authId }) =>
                    getAuthorizationStates(authId, authorizationList).Visible
            )
            .map(({ authId, title, customClassName, component }) => ({
                title,
                customClassName,
                content: getAuthorizationStates(authId, authorizationList)
                    .Consult ? (
                    component
                ) : (
                    <NotAuthorizationPage modal={notAuthModalProps} />
                ),
            }));
    }, [sharedPeripheralProps, oneShop, authorizationList, notAuthModalProps, t]);

    const validSelectedRows = React.useMemo(
        () => selectedRows.filter(
            (elt: any) => elt[t("Application")] !== ""
        ),
        [selectedRows, t]
    );


    function toggleNav(tab: number) {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
        if (isSubMenuCollapsed) {
            setIsSubMenuCollapsed(false);
        }
    }

    function toggleSubNav(tab: number) {
        if (activeSubTab !== tab) {
            setActiveSubTab(tab);
        }
    }

    React.useEffect(() => {
        setCheckModificationEffect(false);
        setIsLoadingAssociateValidation(false);

        const validSelectedRows = selectedRows.filter(
            (elt: any) => elt[t("Application")] !== ""
        );
        const source =
            validSelectedRows.length === 1 ? validSelectedRows[0] : rowData;

        source?.subRows?.forEach((el: any) => {
            if (el.peripheralType === PERIPHERAL_TYPE_MONETIC) {
                const id = el.id_monetic;
                if (!associateStore.monetics.some((m: any) => m.Id === id)) {
                    associateStore.monetics.push({
                        ...el,
                        Id: id,
                        [t("Designation")]: el[t("Peripheral")],
                    });
                }
            }
            if (el.peripheralType === PERIPHERAL_TYPE_CASH_MANAGEMENT) {
                const id = el.peripheralId;
                if (
                    !associateStore.cashManagements.some(
                        (m: any) => m.Id === id
                    )
                ) {
                    associateStore.cashManagements.push({
                        ...el,
                        Id: id,
                        [t("Designation")]: el[t("Peripheral")],
                    });
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <ModalBody className="p-0 modal-associate__clz">
            <div style={{ display: "grid", gridTemplateColumns: "220px 1fr" }}>
                <Nav
                    tabs
                    className="d-flex flex-column"
                    style={{
                        backgroundColor: mainColor,
                        marginBottom: "62px",
                        fontSize: "15px",
                    }}
                >
                    {tabs.map((element, index) => {
                        return (
                            <NavItem
                                key={index}
                                className={classNames(
                                    `pb-1 ${element.customClassName}`,
                                    {
                                        "active menu-wrapper-items-nav-link-active__clz":
                                            activeTab === index,
                                    }
                                )}
                            >
                                <NavLink
                                    className={classNames(
                                        "border-0 menu-wrapper-nav-link__clz",
                                        {
                                            "active menu-wrapper-items-nav-link-active__clz":
                                                activeTab === index,
                                        }
                                    )}
                                    onClick={() => {
                                        toggleNav(index);
                                        setIsAddPeripheralClicked(false);
                                        if (
                                            validSelectedRows.length > 1 &&
                                            (element.title === t("Monetics") ||
                                                element.title === t("Coiners"))
                                        ) {
                                            toast.warning(
                                                element.title === t("Monetics")
                                                    ? t(
                                                          "Attention, you cannot associate one payment system with more than one application."
                                                      )
                                                    : t(
                                                          "Attention, you cannot associate a coin acceptor with more than one application."
                                                      ),
                                                TOAST_OPTIONS
                                            );
                                        }

                                        if (
                                            element.title === t("Scale") ||
                                            element.title === t("Beepers") ||
                                            element.title === t("Smartill")
                                        ) {
                                            setActiveTab(0);
                                        }
                                    }}
                                >
                                    <span className="px-2">
                                        {element.title}
                                    </span>
                                </NavLink>
                                {element.title === t("Scale") &&
                                isSubMenuCollapsed
                                    ? element.subMenu?.map(
                                          (
                                              item: {
                                                  title: string;
                                                  content: React.ReactNode;
                                              },
                                              subMenuIndex: number
                                          ): JSX.Element => (
                                              <NavLink
                                                  disabled={
                                                      item.title ===
                                                      t("Customer account")
                                                  }
                                                  key={subMenuIndex}
                                                  className={classNames(
                                                      "border-0 text-nowrap pl-5 menu-wrapper-nav-link__clz",
                                                      {
                                                          "active options-nav-link-active__clz":
                                                              activeSubTab ===
                                                              subMenuIndex,
                                                      }
                                                  )}
                                                  onClick={() => {
                                                      toggleSubNav(
                                                          subMenuIndex
                                                      );
                                                  }}
                                              >
                                                  <span className="px-2">
                                                      {item.title}
                                                  </span>
                                              </NavLink>
                                          )
                                      )
                                    : null}
                            </NavItem>
                        );
                    })}
                </Nav>
                <TabContent activeTab={activeTab}>
                    {tabs.map((element, index) => {
                        return element.subMenu !== undefined &&
                            isSubMenuCollapsed ? (
                            element.subMenu?.map(
                                ({ content }, subMenuIndex) => {
                                    return (
                                        <TabPane
                                            tabId={index}
                                            className={classNames({
                                                "d-none":
                                                    subMenuIndex !==
                                                    activeSubTab,
                                            })}
                                            style={{
                                                height: "100%",
                                                width: "100%",
                                                overflow: "hidden",
                                            }}
                                            key={subMenuIndex}
                                        >
                                            {activeTab === index
                                                ? content
                                                : null}
                                        </TabPane>
                                    );
                                }
                            )
                        ) : (
                            <TabPane
                                tabId={index}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    overflow: "hidden",
                                }}
                                key={index}
                            >
                                {activeTab === index ? element.content : null}
                            </TabPane>
                        );
                    })}
                </TabContent>
                <LoadingValidationAssociateIcon
                    style={{
                        position: "absolute",
                        left: "44%",
                        top: "30%",
                        zIndex: 10,
                        display: isLoadingAssociateValidation ? "" : "none",
                    }}
                />
            </div>
        </ModalBody>
    );
}
